import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import authService from "../services/auth";

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleOAuthCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setIsProcessing(true);
        setError(null);

        // Get token from URL parameters
        const token = searchParams.get('token');
        const errorParam = searchParams.get('error');

        console.log('🔐 OAuth callback received:', { token: !!token, error: errorParam });

        // Handle error cases
        if (errorParam) {
          let errorMessage = 'Authentication failed';
          switch (errorParam) {
            case 'google_auth_failed':
              errorMessage = 'Google authentication failed. Please try again.';
              break;
            case 'oauth_processing_failed':
              errorMessage = 'OAuth processing failed. Please try again.';
              break;
            default:
              errorMessage = 'Authentication failed. Please try again.';
          }
          throw new Error(errorMessage);
        }

        // Handle missing token
        if (!token) {
          throw new Error('No authentication token received');
        }

        // Process the OAuth callback using AuthContext
        await handleOAuthCallback(token);

        console.log('✅ OAuth callback successful, user should be set in context');

        // The AuthContext handleOAuthCallback will handle redirection automatically
        // No need for manual redirect here

      } catch (error: any) {
        console.error('❌ OAuth callback error:', error);
        setError(error.message || 'Authentication failed');

        // Redirect to login with error after a delay
        setTimeout(() => {
          navigate(`/login?error=${encodeURIComponent(error.message || 'Authentication failed')}`);
        }, 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, handleOAuthCallback]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
              <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              Authentication Failed
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {error}
            </p>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
              Redirecting to login page...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
      <div className="max-w-md w-full mx-auto p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/20">
            <svg className="animate-spin h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
            {isProcessing ? 'Processing Authentication...' : 'Redirecting...'}
          </h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Please wait while we complete your login.
          </p>
        </div>
      </div>
    </div>
  );
}
